import React, { FC, ReactNode } from 'react';
import styled from 'styled-components';

interface MultilineTextProps {
  children: ReactNode;
  enableMultiline?: boolean;
  className?: string;
}

// Styled component with conditional multiline support and responsive word wrapping
const MultilineWrapper = styled.div<{ enableMultiline: boolean }>`
  /* Always prevent UI breaking with long words - RESPONSIVE */
  overflow-wrap: break-word;
  word-wrap: break-word; 
  word-break: break-word;
  hyphens: auto;
  max-width: 100%;
  
  /* Responsive text handling for all devices */
  line-height: clamp(1.2, 2vw, 1.5);
  
  /* Responsive container constraints */
  width: 100%;
  box-sizing: border-box;
  
  /* Mobile-first responsive breakpoints */
  @media (max-width: 480px) {
    /* Mobile phones */
    overflow-wrap: anywhere; /* More aggressive breaking on small screens */
    word-break: break-all; /* Break even within words if needed */
  }
  
  @media (min-width: 481px) and (max-width: 768px) {
    /* Tablets */
    word-break: break-word; /* Standard word breaking */
  }
  
  @media (min-width: 769px) and (max-width: 1024px) {
    /* Desktop */
    word-break: break-word;
  }
  
  @media (min-width: 1025px) {
    /* Large screens & TVs */
    word-break: break-word;
    max-width: 100%; /* Ensure content doesn't overflow on large screens */
  }
  
  ${({ enableMultiline }) => enableMultiline && `
    white-space: pre-wrap;
  `}
`;

/**
 * MultilineText component that conditionally preserves line breaks
 * Used for NEW API comments while maintaining legacy compatibility
 */
const MultilineText: FC<MultilineTextProps> = ({ 
  children, 
  enableMultiline = false,
  className 
}) => (
  <MultilineWrapper 
    enableMultiline={enableMultiline} 
    className={className}
  >
    {children}
  </MultilineWrapper>
);

export default MultilineText;
